import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import Home from "@/pages/Home";
import PageTransition from "@/components/PageTransition";
import JoinedToast from "@/components/JoinedToast";
import RouteSeo from "@/components/RouteSeo";
import AndroidBetaMount from "@/components/AndroidBetaMount";
import { Toaster } from "sonner";

// Route-level code splitting — non-Home pages load on demand.
const AppPage   = lazy(() => import("@/pages/AppPage"));
const Community = lazy(() => import("@/pages/Community"));
const Concierge = lazy(() => import("@/pages/Concierge"));
const Story     = lazy(() => import("@/pages/Story"));
const SafetyPage = lazy(() => import("@/pages/SafetyPage"));
const Invest    = lazy(() => import("@/pages/Invest"));
const InvestorRoom = lazy(() => import("@/pages/InvestorRoom"));
const TcuPartner   = lazy(() => import("@/pages/TcuPartner"));
const Join      = lazy(() => import("@/pages/Join"));
const Vendors   = lazy(() => import("@/pages/Vendors"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function RouteFallback() {
  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center"
      data-testid="route-fallback"
    >
      <div className="w-8 h-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/"          element={<PageTransition><Home /></PageTransition>} />
          <Route path="/app"       element={<PageTransition><AppPage /></PageTransition>} />
          <Route path="/community" element={<PageTransition><Community /></PageTransition>} />
          <Route path="/concierge" element={<PageTransition><Concierge /></PageTransition>} />
          <Route path="/story"     element={<PageTransition><Story /></PageTransition>} />
          <Route path="/safety"    element={<PageTransition><SafetyPage /></PageTransition>} />
          <Route path="/invest"    element={<PageTransition><Invest /></PageTransition>} />
          <Route path="/investor-room" element={<PageTransition><InvestorRoom /></PageTransition>} />
          <Route path="/tcu"           element={<PageTransition><TcuPartner /></PageTransition>} />
          <Route path="/join"      element={<PageTransition><Join /></PageTransition>} />
          <Route path="/vendors"   element={<PageTransition><Vendors /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App grain">
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <AnimatedRoutes />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
        <JoinedToast />
        <AndroidBetaMount />
      </div>
    </HelmetProvider>
  );
}

export default App;
